import clsx from 'clsx'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Badge } from './badge'
import { Button } from './button'
import { Checkbox } from './checkbox'
import { Popover } from './popover'
import { TextInput } from './text-input'
import { type TrackConfig, trackInteraction } from './tracking'

function haveSameValues(left: string[], right: string[]) {
  return left.length === right.length && left.every((value) => right.includes(value))
}

export interface DropdownFilterItem {
  value: string
  label: string
}

export interface DropdownFilterProps {
  label: string
  items: DropdownFilterItem[]
  defaultValue?: string[]
  onChange?: (value: string[]) => void
  onChangeImmediate?: (value: string[]) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  emptyMessage?: string
  maxHeight?: string
  showViewModeToggle?: boolean
  popoverMinWidth?: string
  track?: TrackConfig<{ selectedValues: string[] }>
}

export function DropdownFilter({
  label,
  items,
  defaultValue,
  onChange,
  onChangeImmediate,
  placeholder = 'Search...',
  className,
  disabled = false,
  emptyMessage = 'No items found',
  maxHeight = '250px',
  showViewModeToggle = true,
  popoverMinWidth = '250px',
  track,
}: DropdownFilterProps) {
  // State
  const [selectedValues, setSelectedValues] = useState<string[]>(defaultValue || [])
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState<'all' | 'selected'>('all')

  // Refs
  const searchInputRef = useRef<HTMLInputElement>(null)
  const committedValuesRef = useRef(defaultValue || [])

  // Computed values
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return items
    const query = searchQuery.toLowerCase()
    return items.filter((item) => item.label.toLowerCase().includes(query))
  }, [items, searchQuery])

  const visibleItems = useMemo(() => {
    if (viewMode === 'selected') {
      return filteredItems.filter((item) => selectedValues.includes(item.value))
    }
    return filteredItems
  }, [filteredItems, viewMode, selectedValues])

  const selectedCount = selectedValues.length

  const badgeLabel = selectedCount > 0 ? `${label} (${selectedCount})` : label

  const selectAllState = useMemo(() => {
    const selectedInFiltered = filteredItems.filter((item) =>
      selectedValues.includes(item.value)
    ).length

    if (selectedInFiltered === 0) return { checked: false, indeterminate: false }
    if (selectedInFiltered === filteredItems.length) return { checked: true, indeterminate: false }
    return { checked: false, indeterminate: true }
  }, [filteredItems, selectedValues])

  // Event handlers
  const handleToggleItem = useCallback(
    (itemValue: string) => {
      const newValue = selectedValues.includes(itemValue)
        ? selectedValues.filter((v) => v !== itemValue)
        : [...selectedValues, itemValue]
      setSelectedValues(newValue)
      onChangeImmediate?.(newValue)
    },
    [selectedValues, onChangeImmediate]
  )

  const handleToggleAll = useCallback(() => {
    const { checked } = selectAllState
    const filteredValues = filteredItems.map((item) => item.value)

    let newValue: string[]
    if (checked) {
      // Unselect all filtered items
      newValue = selectedValues.filter((v) => !filteredValues.includes(v))
    } else {
      // Select all filtered items (merge with existing)
      newValue = Array.from(new Set([...selectedValues, ...filteredValues]))
    }
    setSelectedValues(newValue)
    onChangeImmediate?.(newValue)
  }, [filteredItems, selectedValues, selectAllState, onChangeImmediate])

  const handleClear = useCallback(() => {
    setSelectedValues([])
    onChangeImmediate?.([])
  }, [onChangeImmediate])

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }, [])

  const handleViewModeChange = useCallback((mode: 'all' | 'selected') => {
    setViewMode(mode)
  }, [])

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      setOpen(newOpen)
      if (!newOpen) {
        setSearchQuery('')
        setViewMode('all')
        onChange?.(selectedValues)

        const selectionChanged = !haveSameValues(committedValuesRef.current, selectedValues)
        if (selectionChanged && !disabled) {
          trackInteraction(track, { selectedValues })
        }
        committedValuesRef.current = selectedValues
      }
    },
    [selectedValues, onChange, disabled, track]
  )

  // Effects
  useEffect(() => {
    if (open && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [open])

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger className={className}>
        <Badge
          label={badgeLabel}
          color={selectedCount > 0 ? 'mint' : 'white'}
          rightIcon="caret-down"
        />
      </Popover.Trigger>

      <Popover.Content
        side="bottom"
        collisionAvoidance={{ side: 'none' }}
        className={clsx('!p-0', 'max-w-[400px]')}
        style={{ minWidth: popoverMinWidth }}
      >
        {/* Search Section */}
        <div className="px-4 pt-4 pb-2">
          <TextInput
            ref={searchInputRef}
            placeholder={placeholder}
            value={searchQuery}
            onChange={handleSearchChange}
            disabled={disabled}
          />
        </div>

        {/* Toggle Section */}
        {showViewModeToggle && (
          <div className="px-4 py-2 flex items-center gap-2">
            <span className="text-sm text-charcoal-75">Show:</span>
            <Button
              variant="text"
              onClick={() => handleViewModeChange('all')}
              disabled={disabled}
              className={clsx(
                'h-auto px-2 py-1 text-sm',
                viewMode === 'all' ? 'font-bold' : 'font-normal'
              )}
            >
              All
            </Button>
            <span className="text-charcoal-50">|</span>
            <Button
              variant="text"
              onClick={() => handleViewModeChange('selected')}
              disabled={disabled}
              className={clsx(
                'h-auto px-2 py-1 text-sm',
                viewMode === 'selected' ? 'font-bold' : 'font-normal'
              )}
            >
              Selected
            </Button>
          </div>
        )}

        {/* Select All Section */}
        <div className="px-4 py-2 border-b border-gray-200">
          <button
            type="button"
            className="w-full cursor-pointer text-left"
            onClick={handleToggleAll}
            disabled={disabled}
          >
            <div className="flex items-center gap-2">
              <Checkbox
                checked={selectAllState.checked}
                indeterminate={selectAllState.indeterminate}
                disabled={disabled}
                className="pointer-events-none"
              />
              <span className="text-sm">Select All</span>
            </div>
          </button>
        </div>

        {/* List Section */}
        <div className="overflow-y-auto" style={{ maxHeight }}>
          {visibleItems.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-charcoal-50">{emptyMessage}</div>
          ) : (
            visibleItems.map((item) => (
              <button
                key={item.value}
                type="button"
                className={clsx(
                  'w-full px-4 py-2 cursor-pointer text-left',
                  selectedValues.includes(item.value) && 'bg-mint-25'
                )}
                onClick={() => handleToggleItem(item.value)}
                disabled={disabled}
              >
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={selectedValues.includes(item.value)}
                    disabled={disabled}
                    className="pointer-events-none"
                  />
                  <span className="text-sm">{item.label}</span>
                </div>
              </button>
            ))
          )}
        </div>

        {/* Clear Section */}
        {selectedCount > 0 && (
          <div className="px-4 pt-3 pb-4 border-t border-gray-200">
            <Button variant="link" onClick={handleClear} disabled={disabled} className="text-sm">
              Clear selection
            </Button>
          </div>
        )}

        {/* Screen reader status */}
        <output aria-live="polite" aria-atomic="true" className="sr-only">
          {selectedCount} items selected
        </output>
      </Popover.Content>
    </Popover.Root>
  )
}
